import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

/**
 * PreLaunchGuard - Protège le site en mode pré-lancement
 *
 * Comportement runtime:
 * - Le mode pré-lancement est piloté par le backend via GET /api/public/site-status
 * - Fail-safe: en cas d'erreur API, on considère pre_launch_mode=true (site fermé)
 * - Cache local court (30s) pour éviter de spammer l'API à chaque route
 * - Visiteurs non connectés → Page "Coming Soon" si pre_launch_mode=true
 * - Utilisateur connecté → Accès complet
 */

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_TTL_MS = 30 * 1000; // 30 secondes

// Cache module-level partagé — Phase I : contient maintenant la config
// complète (pre_launch_mode + public_membership_enabled). On conserve
// `_cache` historique pour compatibilité amont (useSiteStatus booléen).
let _cache = { value: null, expiresAt: 0 };
let _configCache = { value: null, expiresAt: 0 };

async function fetchSiteConfig() {
  const now = Date.now();
  if (_configCache.value !== null && now < _configCache.expiresAt) {
    return _configCache.value;
  }
  try {
    const res = await axios.get(`${API}/api/public/site-status`, { timeout: 5000 });
    const cfg = {
      pre_launch_mode: Boolean(res?.data?.pre_launch_mode),
      public_membership_enabled: Boolean(res?.data?.public_membership_enabled),
      membership_benefits_enabled: Boolean(res?.data?.membership_benefits_enabled),
    };
    _configCache = { value: cfg, expiresAt: now + CACHE_TTL_MS };
    // Compat `_cache` booléen.
    _cache = { value: cfg.pre_launch_mode, expiresAt: now + CACHE_TTL_MS };
    return cfg;
  } catch (_e) {
    const cfg = { pre_launch_mode: true, public_membership_enabled: false, membership_benefits_enabled: false };
    _configCache = { value: cfg, expiresAt: now + CACHE_TTL_MS };
    _cache = { value: true, expiresAt: now + CACHE_TTL_MS };
    return cfg;
  }
}

async function fetchSiteStatus() {
  const cfg = await fetchSiteConfig();
  return cfg.pre_launch_mode;
}

// Routes toujours accessibles même en mode pré-lancement
const PUBLIC_ROUTES = [
  "/connexion",
  "/coming-soon",
  "/mentions-legales",
  "/confidentialite",
  "/mot-de-passe-oublie",
  "/reinitialiser-mot-de-passe"
];

export function PreLaunchGuard({ children }) {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preLaunch, setPreLaunch] = useState(true); // défaut fail-safe

  useEffect(() => {
    let alive = true;
    fetchSiteStatus().then((value) => {
      if (alive) {
        setPreLaunch(value);
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  // Pendant le chargement: loader neutre
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  // Mode pré-lancement désactivé → tout le monde accède
  if (!preLaunch) {
    return children;
  }

  // Utilisateur connecté → accès complet
  if (isAuthenticated) {
    return children;
  }

  // Sinon, rediriger vers Coming Soon
  return <Navigate to="/coming-soon" replace />;
}

export function isPreLaunchRoute(pathname) {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
}

/**
 * useSiteStatus — hook partagé exposant pre_launch_mode (réutilise le cache
 * module-level de fetchSiteStatus). Défaut fail-safe `true` (site fermé) tant
 * que la réponse backend n'est pas arrivée : évite tout flash de navigation
 * publique sur les pages légales en mode pré-lancement.
 */
// Phase I (2026-09-21) — flag public_membership_enabled (DISTINCT de
// pre_launch_mode). Par défaut `false` : la promotion publique de l'adhésion
// est masquée (CTA Adhérer, avantages mis en avant, pages de souscription
// hors navigation publique). Le système Membership interne reste intact.
// Phase I.b (2026-10-04) — flag membership_benefits_enabled (DISTINCT de
// pre_launch_mode et public_membership_enabled). Par défaut `false` : les
// promesses commerciales inactives (réductions -15%, bons plans adhérents
// exclusifs, tarifs préférentiels…) sont masquées. L'adhésion elle-même
// continue de fonctionner (/adherer, formulaires, paiements, carte, wallet).
export function useMembershipBenefitsEnabled() {
  const [enabled, setEnabled] = useState(_configCache.value?.membership_benefits_enabled ?? false);
  useEffect(() => {
    let alive = true;
    fetchSiteConfig().then((cfg) => { if (alive) setEnabled(!!cfg.membership_benefits_enabled); });
    return () => { alive = false; };
  }, []);
  return enabled;
}

export function usePublicMembershipEnabled() {
  const [enabled, setEnabled] = useState(_configCache.value?.public_membership_enabled ?? false);
  useEffect(() => {
    let alive = true;
    fetchSiteConfig().then((cfg) => { if (alive) setEnabled(!!cfg.public_membership_enabled); });
    return () => { alive = false; };
  }, []);
  return enabled;
}

export function useSiteStatus() {
  const [preLaunch, setPreLaunch] = useState(
    _cache.value !== null && Date.now() < _cache.expiresAt ? _cache.value : true
  );
  useEffect(() => {
    let alive = true;
    fetchSiteStatus().then((value) => { if (alive) setPreLaunch(value); });
    return () => { alive = false; };
  }, []);
  return preLaunch;
}
